import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumCostPriceUpdateType } from "./enums/enumCostPriceUpdateType.generated";
import { EnumEprProducerStatus } from "./enums/enumEprProducerStatus.generated";
import { EnumPurchaseOrderDeliveryDisbursement } from "./enums/enumPurchaseOrderDeliveryDisbursement.generated";

// Model Objects

export class TenantSettings extends ModelObject<TenantSettings> {

    public version: number = 0;
    public tenantId: Guid = Guid.createEmpty();
    /**
    * Controls the Mega Batches status; when true, Production is disabled.
    */
    public isMegaBatchesEnabled: boolean = false;
    /**
    * Specifies the duration in months for which a product batch can be displayed in the system.
    * Minimum : 1
    */
    public productBatchDuration: number = 0;
    /**
    * If enabled, allows hop alpha for materials.
    */
    public usesHopAlpha: boolean = false;
    public poDefaultDeliveryDisbursement: EnumPurchaseOrderDeliveryDisbursement = EnumPurchaseOrderDeliveryDisbursement.none;
    private _poValueRequiringApproval: number | null = null;
    public get poValueRequiringApproval(): number | null { return this._poValueRequiringApproval }
    public set poValueRequiringApproval(value: number | null) {
      this._poValueRequiringApproval = value === undefined ? null : value;
    }
    /**
    * If enabled, allows regional tax for outlets and calculate additional tax to orders.
    */
    public usesRegionalTax: boolean = false;
    /**
    * Default Cost Price update to use for prefilling update dialog. : 0 = KeepCurrent, 1 = LatestKnown, 2 = RollingAverage
    */
    public costPriceUpdateType: EnumCostPriceUpdateType = EnumCostPriceUpdateType.keepCurrent;
    /**
    * If enabled, shows the tenant indicator on the UI. Useful for groups of affilliated tenants.
    */
    public showTenantIndicator: boolean = false;
    public tenantIndicatorColour: string = "";
    public tenantIndicatorText: string = "";
    /**
    * Default duration for a Sales Visit.
    * Not Negative
    */
    public salesVisitDefaultMinutes: number = 0;
    /**
    * The status of the producer dictates what data to show for EPR reporting. : 0 = None, 1 = SmallProducer, 2 = LargeProducer
    */
    public eprProducerStatus: EnumEprProducerStatus = EnumEprProducerStatus.none;
    /**
    * If enabled, allows generation of invoice number on dispatch note creation (print or email).
    */
    public generateInvoiceNumberOnDispatchNoteCreation: boolean = false;
    /**
    * If enabled, outlets which are configured for invoice emails will have them sent automatically when orders are marked as complete.
    */
    public emailInvoicesOnOrderCompletion: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.isMegaBatchesEnabled = jsonSerializedObject.isMegaBatchesEnabled;
          this.productBatchDuration = jsonSerializedObject.productBatchDuration;
          this.usesHopAlpha = jsonSerializedObject.usesHopAlpha;
          this.poDefaultDeliveryDisbursement = EnumPurchaseOrderDeliveryDisbursement.getByValue(jsonSerializedObject.poDefaultDeliveryDisbursement, this.poDefaultDeliveryDisbursement);
          this.poValueRequiringApproval = jsonSerializedObject.poValueRequiringApproval == null ? null : jsonSerializedObject.poValueRequiringApproval;
          this.usesRegionalTax = jsonSerializedObject.usesRegionalTax;
          this.costPriceUpdateType = EnumCostPriceUpdateType.getByValue(jsonSerializedObject.costPriceUpdateType, this.costPriceUpdateType);
          this.showTenantIndicator = jsonSerializedObject.showTenantIndicator;
          this.tenantIndicatorColour = jsonSerializedObject.tenantIndicatorColour;
          this.tenantIndicatorText = jsonSerializedObject.tenantIndicatorText;
          this.salesVisitDefaultMinutes = jsonSerializedObject.salesVisitDefaultMinutes;
          this.eprProducerStatus = EnumEprProducerStatus.getByValue(jsonSerializedObject.eprProducerStatus, this.eprProducerStatus);
          this.generateInvoiceNumberOnDispatchNoteCreation = jsonSerializedObject.generateInvoiceNumberOnDispatchNoteCreation;
          this.emailInvoicesOnOrderCompletion = jsonSerializedObject.emailInvoicesOnOrderCompletion;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.tenantId = this.tenantId.toString();
        toRet.isMegaBatchesEnabled = this.isMegaBatchesEnabled;
        toRet.productBatchDuration = this.productBatchDuration;
        toRet.usesHopAlpha = this.usesHopAlpha;
        toRet.poDefaultDeliveryDisbursement = this.poDefaultDeliveryDisbursement.value;
        toRet.poValueRequiringApproval = this.poValueRequiringApproval == null ? null : this.poValueRequiringApproval;
        toRet.usesRegionalTax = this.usesRegionalTax;
        toRet.costPriceUpdateType = this.costPriceUpdateType.value;
        toRet.showTenantIndicator = this.showTenantIndicator;
        toRet.tenantIndicatorColour = this.tenantIndicatorColour;
        toRet.tenantIndicatorText = this.tenantIndicatorText;
        toRet.salesVisitDefaultMinutes = this.salesVisitDefaultMinutes;
        toRet.eprProducerStatus = this.eprProducerStatus.value;
        toRet.generateInvoiceNumberOnDispatchNoteCreation = this.generateInvoiceNumberOnDispatchNoteCreation;
        toRet.emailInvoicesOnOrderCompletion = this.emailInvoicesOnOrderCompletion;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "TenantSettings.TenantId cannot be empty"},
            {field: "productBatchDuration", rule: (v: any) => v == null || v >= 1 || "ProductBatchDuration is not greater than minimum allowed value: 1"},
            {field: "productBatchDuration", rule: (v: any) => v == null || v <= 2147483647 || "ProductBatchDuration is above maximum allowed value: 2147483647"},
            {field: "salesVisitDefaultMinutes", rule: (v: any) => v == null || v >= 0 || "SalesVisitDefaultMinutes is not greater than minimum allowed value: 0"},
            {field: "salesVisitDefaultMinutes", rule: (v: any) => v == null || v <= 2147483647 || "SalesVisitDefaultMinutes is above maximum allowed value: 2147483647"},
    ];
}


export class TenantSettingsService extends ServiceBase {

  public async getTenantSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<TenantSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new TenantSettings(response.tenantSettings);;
    return this.makeJsonRequest("tenantSettings/v1/getTenantSettings", req, false, extractor, opts);
  };

  public async setIsMegaBatchesEnabled(enable: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    enable:enable,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/setIsMegaBatchesEnabled", req, true, extractor, opts);
  };

  public async setProductBatchDuration(monthsToDisplay: number, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    monthsToDisplay:monthsToDisplay,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/setProductBatchDuration", req, true, extractor, opts);
  };

  public async savePurchaseOrderSettings(poDefaultDeliveryDisbursement: EnumPurchaseOrderDeliveryDisbursement, poValueRequiringApproval: number | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    poDefaultDeliveryDisbursement:poDefaultDeliveryDisbursement.value,
    poValueRequiringApproval:poValueRequiringApproval == null ? null : poValueRequiringApproval,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/savePurchaseOrderSettings", req, true, extractor, opts);
  };

  public async saveProductionSettings(usesHopAlpha: boolean, costPriceUpdateType: EnumCostPriceUpdateType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    usesHopAlpha:usesHopAlpha,
    costPriceUpdateType:costPriceUpdateType.value,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/saveProductionSettings", req, true, extractor, opts);
  };

  public async saveTaxSettings(usesRegionalTax: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    usesRegionalTax:usesRegionalTax,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/saveTaxSettings", req, true, extractor, opts);
  };

  public async saveDisplaySettings(showTenantIndicator: boolean, tenantIndicatorColour: string, tenantIndicatorText: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    showTenantIndicator:showTenantIndicator,
    tenantIndicatorColour:tenantIndicatorColour,
    tenantIndicatorText:tenantIndicatorText,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/saveDisplaySettings", req, true, extractor, opts);
  };

  public async saveSalesVisitSettings(salesVisitDefaultMinutes: number, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesVisitDefaultMinutes:salesVisitDefaultMinutes,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/saveSalesVisitSettings", req, true, extractor, opts);
  };

  public async saveEprSettings(eprProducerStatus: EnumEprProducerStatus, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    eprProducerStatus:eprProducerStatus.value,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/saveEprSettings", req, true, extractor, opts);
  };

  public async saveInvoiceSettings(generateInvoiceNumberOnDispatchNoteCreation: boolean, emailInvoicesOnOrderCompletion: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    generateInvoiceNumberOnDispatchNoteCreation:generateInvoiceNumberOnDispatchNoteCreation,
    emailInvoicesOnOrderCompletion:emailInvoicesOnOrderCompletion,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/saveInvoiceSettings", req, true, extractor, opts);
  };
}

export const tenantSettingsService = new TenantSettingsService();